import * as CognitoAuth from 'aws-amplify/auth';
import type { CreateUserRequest, CurrentUserModel } from '@/models';
import { createUser } from './userService';
import { getErrorMessage } from '@/utils';
import type { SignInResponse } from '@/types/AuthResponses';

async function signIn(username: string, password: string): Promise<SignInResponse> {
  const response: SignInResponse = {
    status: 'UNKNOWN'
  };

  try {
    const authResponse = await CognitoAuth.signIn({
      username: username,
      password: password,
    });

    console.log('authResponse', authResponse);

    switch (authResponse.nextStep.signInStep) {
      case 'CONFIRM_SIGN_UP':
        response.status = 'CONFIRM_SIGNUP';
        break;
      case 'RESET_PASSWORD':
        response.status = 'SUCCESS_PASSWORDRESETREQUIRED'
        break;
      case 'DONE':
        response.status = 'SUCCESS';
        break;
      default:
        response.status = 'UNKNOWN';
    }
  } catch (err) {
    const errMessage = err as string;

    if (errMessage.indexOf('Incorrect username or password') > 0) {
      response.status = 'INVALID_CREDENTIALS';
    } else {
      throw new Error('Error logging in');
    }
  }

  return response;
}

async function signOut() {
  try {
    await CognitoAuth.signOut();
  } catch (err) {
    throw new Error(err as string);
  }
}

async function getAccessToken(): Promise<string | null> {
  try {
    const session = await CognitoAuth.fetchAuthSession();
    const token = session?.tokens?.accessToken.toString() || null;
    return token;
  } catch {
    return null;
  }
}

async function hasAccessToken(): Promise<boolean> {
  const accessToken = await getAccessToken();
  return !!accessToken;
}

async function isAuthenticated(): Promise<boolean> {
  try {
    return await hasAccessToken();
  } catch {
    return false;
  }
}

async function getCurrentUser(): Promise<CurrentUserModel | null> {
  try {
    if (!await isAuthenticated()) {
      return null;
    }

    const claims = await getCurrentUserClaims();
    if (!claims) {
      return null;
    }

    const user: CurrentUserModel = {
      firstName: claims['given_name'],
      lastName: claims['family_name'],
      emailAddress: claims['email'],
    };

    return user;
  } catch {
    return null;
  }
}

async function getCurrentUserClaims(): Promise<any> {
  try {
    const session = await CognitoAuth.fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No idToken found');
    }

    // Decode the token
    const [ , payloadBase64enc ] = idToken.split('.');
    const payload = JSON.parse(atob(payloadBase64enc));

    return payload;
  } catch {
    throw new Error('Could not get user\'s claims.');
  }
}

async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  try {
    await CognitoAuth.updatePassword({
      oldPassword: currentPassword,
      newPassword: newPassword,
    });
  } catch (err) {
    const errMessage = getErrorMessage(err);
    if (errMessage.includes('Incorrect username or password')) {
      throw new Error('Current password is invalid');
    } else {
      throw new Error('Error changing password');
    }
  }
}

async function recoverPassword(email: string): Promise<void> {
  throw new Error('Need to re-implement');
  try {
    // await CognitoService.forgotPassword(email);
  } catch {
    throw new Error('Error submitting password recovery.');
  }
}

async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  throw new Error('Need to re-implement');

  try {
    // await CognitoService.forgotPasswordSubmit(email, code, newPassword);
  } catch {
    throw new Error('Error resetting user\'s password');
  }
}

export async function register(userModel: CreateUserRequest): Promise<CurrentUserModel> {
  // throw new Error('Need to re-implement');

  try {
    const signupResponse = await CognitoAuth.signUp({
      username: userModel.emailAddress,
      password: userModel.password,
      options: {
         userAttributes: {
          given_name: userModel.firstName,
          family_name: userModel.lastName,
          email: userModel.emailAddress,
         }
      }
    })

    console.log('signupResponse', signupResponse);
    return null as any

    // const createdUser = await createUser(userModel);

    // signupResponse./
    // if (createdUser) {
    //   // Set user's email address in session storage to use for confirming account
    //   window.sessionStorage .setItem('__newEmail__', createdUser.emailAddress);

    //   return createdUser;
    // } else {
    //   throw new Error('Error registering user');
    // }
  } catch (error) {
    console.error('Error registering user', error);
    throw error;
  }
}

/**
 * Confirms a user's account in the IDP
 * @param email User's email address/username
 * @param code The confirmation code
 */
export async function confirmAccount(email: string, code: string): Promise<any> {
  try {
    await CognitoAuth.confirmSignUp({
      username: email,
      confirmationCode: code,
    })
  } catch {
    throw new Error('Error confirming user signup.');
  }
}

/**
 *
 * @deprecated Prefer using `confirmAccount`
 */
export async function verifyEmail(verificationCode: string): Promise<any> {
  throw new Error('Need to re-implement');

  try {
    const emailAddress = window.sessionStorage.getItem('__newEmail__');
    if (!emailAddress) {
      throw new Error('Could not retrieve email for signup from sessionStorage');
    }
    // await CognitoService.confirmSignUp(emailAddress, verificationCode);

    // Clear the storage item out if successful
    window.sessionStorage.removeItem('__newEmail__');
  } catch (error) {
    console.error('Error confirming email', error);
    throw new Error('Error confirming email');
  }
}

export {
  signIn,
  signOut,
  getAccessToken,
  hasAccessToken,
  isAuthenticated,
  getCurrentUser,
  changePassword,
  recoverPassword,
  resetPassword,
}
